import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSiteImages } from "@/lib/siteImages";
import { useLang } from "@/lib/i18n";

const IMG = {
  energy:
    "https://images.unsplash.com/photo-1698752822107-69f8973936e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVscyUyMHdpbmQlMjB0dXJiaW5lcyUyMHN1bnNldHxlbnwwfHx8fDE3ODgyMjQxMTV8MA&ixlib=rb-4.1.0&q=85",
  minerals:
    "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1200&q=80",
  healthcare:
    "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&w=1200&q=80",
  machinery:
    "https://images.unsplash.com/photo-1565377167263-d29b5ac85479?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwbWFjaGluZXJ5JTIwZmFjdG9yeXxlbnwwfHx8fDE3ODgyMjQxMDF8MA&ixlib=rb-4.1.0&q=85",
  agriculture:
    "https://images.pexels.com/photos/27037415/pexels-photo-27037415.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  textiles:
    "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80",
  construction:
    "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80",
  trade:
    "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1600&q=80",
};

const META = [
  { img: "energy", span: "md:col-span-7", h: "h-80", links: ["crude-oil", "petroleum-products", "lng", "lpg", null] },
  { img: "minerals", span: "md:col-span-5", h: "h-80", links: ["iron-ore", "gold", "manganese", "copper", null] },
  { img: "healthcare", span: "md:col-span-4", h: "h-72", links: ["medicines-pharmaceuticals", null, "medical-devices", null, null] },
  { img: "machinery", span: "md:col-span-4", h: "h-72", links: [], page: "industrial-machinery" },
  { img: "agriculture", span: "md:col-span-4", h: "h-72", links: [], page: "agricultural-commodities" },
  { img: "textiles", span: "md:col-span-6", h: "h-72", links: [], page: "textiles-garments" },
  { img: "construction", span: "md:col-span-6", h: "h-72", links: [], page: "construction-materials" },
  { img: "trade", span: "md:col-span-12", h: "h-64", links: [] },
];

export const Industries = () => {
  const si = useSiteImages();
  const { t } = useLang();
  return (
  <section id="industries" data-testid="industries-section" className="py-24 md:py-32 bg-surface">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <Eyebrow>{t.industries.eyebrow}</Eyebrow>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] max-w-xl">
            {t.industries.title}
          </h2>
          <p className="text-base text-[#94A3B8] max-w-sm">
            {t.industries.sub}
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {t.industries.list.map((ind, i) => {
          const meta = META[i];
          return (
            <Reveal key={i} delay={(i % 3) * 0.07} className={meta.span}>
              <div
                data-testid={`industry-card-${i + 1}`}
                className={`group relative overflow-hidden border border-[#1E293B] ${meta.h} hover:border-gold/50 transition-[border-color] duration-300`}
              >
                <img
                  src={si(`industry-${meta.img}`, IMG[meta.img])}
                  alt={ind.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#060A14]/75 group-hover:bg-[#060A14]/55 transition-colors duration-500" />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#060A14] via-[#060A14]/60 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-7 md:p-8">
                  <span className="font-display text-sm text-gold/80 mb-2">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-xl md:text-2xl font-semibold text-[#F4F6F8] mb-3 flex items-center gap-2">
                    {meta.page ? (
                      <Link
                        to={`/commodities/${meta.page}`}
                        data-testid={`industry-page-link-${meta.page}`}
                        className="flex items-center gap-2 hover:text-gold transition-colors duration-300"
                      >
                        {ind.name}
                        <ArrowUpRight className="w-5 h-5 text-gold opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-[opacity,transform] duration-300" />
                      </Link>
                    ) : (
                      <>
                        {ind.name}
                        <ArrowUpRight className="w-5 h-5 text-gold opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-[opacity,transform] duration-300" />
                      </>
                    )}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {ind.items.map((item, j) => {
                      const slug = meta.links[j];
                      const chipCls = "text-[11px] uppercase tracking-wider px-2.5 py-1 border";
                      return slug ? (
                        <Link
                          key={item}
                          to={`/commodities/${slug}`}
                          data-testid={`industry-link-${slug}`}
                          className={`${chipCls} text-[#C6D0DD] border-white/15 hover:border-gold hover:text-gold transition-colors duration-200`}
                        >
                          {item}
                        </Link>
                      ) : (
                        <span key={item} className={`${chipCls} text-[#C6D0DD] border-white/15`}>
                          {item}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
  );
};
